// Leitura e gravação do painel "Saldos das UCs". A regra fica em `saldosUcs.ts`
// (puro, testado); aqui é só Supabase.

import { supabase } from '../../../src/lib/supabase';
import { RateioAjusteStatus, RateioAjusteUc, RateioCredito, RateioUnidadeGeradora } from '../../../types';
import { buscarHistoricoConsumoUcs, type FaturaConsumoResumo } from './consumoHistorico';
import { mesReferenciaParaOrdinal } from './mesReferenciaRateio';
import { mediaGeracao, normalizarGeradora, type ItemRateioPainel, type UcPainel, type UgPainel } from './saldosUcs';

// PAGINAÇÃO OBRIGATÓRIA: o PostgREST corta em 1.000 linhas sem erro e sem aviso,
// e o rateio de AGO/2026 já tem 1.057.
const PAGINA = 1000;

async function paginar<T>(consulta: (de: number, ate: number) => PromiseLike<{ data: T[] | null; error: any }>): Promise<T[]> {
  const linhas: T[] = [];
  for (let de = 0; ; de += PAGINA) {
    const { data, error } = await consulta(de, de + PAGINA - 1);
    if (error) throw error;
    linhas.push(...(data || []));
    if (!data || data.length < PAGINA) break;
  }
  return linhas;
}

/**
 * Rateios do Rateio Antigo, do mês mais recente para o mais antigo. O mês é texto
 * livre ("AGO/2026") e a ordem de upload não é a ordem dos meses (JUL/2026 subiu
 * depois de AGO/2026), por isso ordena pelo mês, não por `created_at`.
 */
export async function listarRateios(): Promise<RateioCredito[]> {
  const { data, error } = await supabase.from('rateios_credito').select('id, mes_referencia, usuario_id, usuario_nome, created_at');
  if (error) throw error;
  return [...((data || []) as RateioCredito[])].sort((a, b) => {
    const diff = (mesReferenciaParaOrdinal(b.mes_referencia) ?? -Infinity) - (mesReferenciaParaOrdinal(a.mes_referencia) ?? -Infinity);
    return diff !== 0 ? diff : b.created_at.localeCompare(a.created_at);
  });
}

export async function carregarItensRateio(rateioId: string): Promise<ItemRateioPainel[]> {
  return paginar<ItemRateioPainel>((de, ate) =>
    supabase
      .from('rateios_credito_itens')
      .select('id, uc, outro_numero_uc, status, nome, cpf_cnpj, uc_geradora, percent_rateio')
      .eq('rateio_id', rateioId)
      .order('id', { ascending: true })
      .range(de, ate)
  );
}

const COLUNAS_UC =
  'id, numero_uc, outro_numero_uc, nome_cliente, situacao, tipo_ligacao, rateio_travado_em, ' +
  'consumo_medio_kwh, consumo_observado_kwh, consumo_observado_meses, consumo_observado_em, consumo_observado_dispersao';

/** Cadastro inteiro (≈1.750 UCs, poucas colunas): o cruzamento pelos dois números é feito em `saldosUcs.ts`. */
export async function carregarUcs(): Promise<UcPainel[]> {
  return paginar<UcPainel>((de, ate) =>
    supabase.from('unidades_consumidoras').select(COLUNAS_UC).order('id', { ascending: true }).range(de, ate) as any
  );
}

export async function carregarHistorico(ucs: UcPainel[]): Promise<Map<number, FaturaConsumoResumo[]>> {
  return buscarHistoricoConsumoUcs(
    ucs.map(u => ({ id: u.id, numero_uc: u.numero_uc || '', outro_numero_uc: u.outro_numero_uc })),
    { mesesLookback: 6 }
  );
}

export type GeradorasPainel = { ugs: UgPainel[]; cadastro: RateioUnidadeGeradora[] };

/** Usinas com a média de geração registrada (base do % sugerido). */
export async function carregarGeradoras(): Promise<GeradorasPainel> {
  const [{ data: ugsData, error: ugsErr }, geracao] = await Promise.all([
    supabase.from('rateio_unidades_geradoras').select('*').order('nome'),
    paginar<{ rateio_unidade_geradora_id: string; mes_referencia: string; geracao_kwh: number }>((de, ate) =>
      supabase
        .from('rateio_geracao_mensal')
        .select('rateio_unidade_geradora_id, mes_referencia, geracao_kwh')
        .order('id', { ascending: true })
        .range(de, ate)
    ),
  ]);
  if (ugsErr) throw ugsErr;
  const cadastro = (ugsData || []) as RateioUnidadeGeradora[];
  const ugs: UgPainel[] = cadastro.map(ug => {
    const media = mediaGeracao(geracao.filter(g => g.rateio_unidade_geradora_id === ug.id));
    return {
      id: ug.id,
      nome: ug.nome,
      numero_uc_geradora: ug.numero_uc_geradora,
      numero_uc_geradora_antigo: ug.numero_uc_geradora_antigo,
      mediaGeracaoKwh: media.mediaKwh,
      mesesGeracao: media.meses,
    };
  });
  return { ugs, cadastro };
}

export async function carregarAjustes(): Promise<RateioAjusteUc[]> {
  return paginar<RateioAjusteUc>((de, ate) =>
    supabase.from('rateio_ajustes_uc').select('*').order('created_at', { ascending: false }).range(de, ate)
  );
}

export type NovoAjuste = Omit<
  RateioAjusteUc,
  'id' | 'status' | 'created_at' | 'updated_at' | 'status_atualizado_por_nome' | 'status_atualizado_em'
>;

export async function criarAjuste(ajuste: NovoAjuste): Promise<RateioAjusteUc> {
  const { data, error } = await supabase.from('rateio_ajustes_uc').insert(ajuste).select('*').single();
  if (error) throw error;
  return data as RateioAjusteUc;
}

export async function atualizarStatusAjuste(id: string, status: RateioAjusteStatus, quem: string | null): Promise<RateioAjusteUc> {
  const { data, error } = await supabase
    .from('rateio_ajustes_uc')
    .update({ status, status_atualizado_por_nome: quem, status_atualizado_em: new Date().toISOString() })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as RateioAjusteUc;
}

/** Histórico de % e usina de uma UC em todos os rateios (aberto no detalhe da UC). */
export async function carregarHistoricoRateiosDaUc(
  numeros: string[],
  rateios: RateioCredito[]
): Promise<{ mes: string; status: string | null; ugNumero: string | null; percent: number | null }[]> {
  const alvos = numeros.filter(Boolean);
  if (alvos.length === 0) return [];
  const lista = alvos.join(',');
  const { data, error } = await supabase
    .from('rateios_credito_itens')
    .select('rateio_id, status, uc_geradora, percent_rateio')
    .or(`uc.in.(${lista}),outro_numero_uc.in.(${lista})`);
  if (error) throw error;
  const mesPorRateio = new Map(rateios.map(r => [r.id, r.mes_referencia]));
  return (data || [])
    .map((i: any) => ({
      mes: mesPorRateio.get(i.rateio_id) || '—',
      status: i.status ?? null,
      ugNumero: normalizarGeradora(i.uc_geradora),
      percent: i.percent_rateio == null ? null : Number(i.percent_rateio),
    }))
    .sort((a, b) => (mesReferenciaParaOrdinal(b.mes) ?? -Infinity) - (mesReferenciaParaOrdinal(a.mes) ?? -Infinity));
}
